export enum AudienceSourceType {
  Unspecified = 'AUDIENCE_SOURCE_TYPE_UNSPECIFIED',
  Segment = 'AUDIENCE_SOURCE_TYPE_SEGMENT',
  Table = 'AUDIENCE_SOURCE_TYPE_TABLE',
}

export enum ContentConfigStatus {
  Unspecified = 'CONTENT_CONFIG_STATUS_UNSPECIFIED',
  Active = 'CONTENT_CONFIG_STATUS_ACTIVE',
  PendingApproval = 'CONTENT_CONFIG_STATUS_PENDING_APPROVAL',
  Rejected = 'CONTENT_CONFIG_STATUS_REJECTED',
  Recalled = 'CONTENT_CONFIG_STATUS_RECALLED',
}

export type EpochSeconds = string | number;

export interface AudiencePersonaVariable {
  name: string;
  description: string;
}

export interface AudienceDeeplink {
  names: string[];
  deepLinkTemplate: string;
}

export interface AudienceFormValue {
  country: string;
  deeplink: AudienceDeeplink;
  description: string;
  isDraft: boolean;
  name: string;
  personaVariables: AudiencePersonaVariable[];
  source: string;
  type?: AudienceSourceType;
}

export interface AudienceValidationErrors {
  country?: string;
  deepLinkTemplate?: string;
  deeplinkNames?: string;
  description?: string;
  name?: string;
  personaVariableDescriptions?: Record<string, string | undefined>;
  personaVariables?: string;
  source?: string;
  type?: string;
}

export interface AudienceColumnOption {
  key: string;
  desc: string;
}

export interface AudienceApiError {
  message?: string;
  status?: number;
}

export interface AudienceTableInfoState {
  columns?: string[];
  error?: AudienceApiError | null;
  isLoading?: boolean;
}

export interface ContentConfigMetaApi {
  id: string;
  entity: string;
  country: string;
  key: string;
  status: ContentConfigStatus;
  expires_at?: EpochSeconds;
  created_at?: EpochSeconds;
  updated_at?: EpochSeconds;
  created_by?: string;
  updated_by?: string;
  reviewed_at?: EpochSeconds;
  reviewed_by?: string;
  review_comment?: string;
}

export type AudiencePersonaVariableApi = AudiencePersonaVariable;

export interface AudienceDeeplinkApi {
  names: string[];
  deep_link_template: string;
}

export interface AudienceBodyApi {
  type: AudienceSourceType;
  name: string;
  description: string;
  source: string;
  persona_variables?: AudiencePersonaVariableApi[];
  deeplink?: AudienceDeeplinkApi;
}

export interface AudienceConfigApi {
  meta: ContentConfigMetaApi;
  body: AudienceBodyApi;
}

export interface ListAudiencesResponseApi {
  configs?: AudienceConfigApi[];
  next_page_token?: string;
}

export interface AudienceTableInfoResponseApi {
  columns?: string[];
}

// ---- Events (Seasonality Phase 1, RTC-33869) ----
// start_date and end_date are INCLUSIVE calendar dates in EVENT_DATE_FORMAT
// ("YYYY-MM-DD") — not epoch seconds like every ContentConfigMetaApi
// timestamp. The event's country lives on the meta, not in the body.

export interface EventBodyApi {
  start_date: string;
  end_date: string;
  description: string;
  name: string;
}

export interface EventConfigApi {
  meta: ContentConfigMetaApi;
  body: EventBodyApi;
}

export interface ListEventsResponseApi {
  configs?: EventConfigApi[];
  next_page_token?: string;
}

export interface EventFormValue {
  country: string;
  description: string;
  endDate: string;
  isDraft: boolean;
  name: string;
  startDate: string;
}

export interface EventValidationErrors {
  country?: string;
  dateRange?: string;
  description?: string;
  name?: string;
}

export interface ProductLangConfigApi {
  name: string;
  ci_enabled: boolean;
  product_overview: string;
  reason_to_use: string;
  call_to_action: string[];
}

// `lang` (RTC-33866) is the optional language this Product Offering targets with
// this Audience. UCD validates it at write time by exact string match against
// `countries[<code>].languages` and never normalises, so it must round-trip
// byte-for-byte. Absent/'' means the association is not language-targeted.
export interface ProductAudienceAssociationApi {
  audience_key: string;
  reason_to_use?: string;
  lang?: string;
}

// Funnel objective as exposed by the UCD Product Offering HTTP contract
// (RTC-33544). The public identifier field is uppercase `ID`; clients omit it
// for a new objective and UCD mints the UUID.
export interface ProductObjectiveApi {
  ID?: string;
  name: string;
  description: string;
  source: string;
}

export interface ProductBodyApi {
  name: string;
  vertical: string;
  deeplink?: string;
  config_by_lang: Record<string, ProductLangConfigApi>;
  cvps?: string[];
  attributes?: string[];
  personas?: string[];
  audiences?: ProductAudienceAssociationApi[];
  objectives?: ProductObjectiveApi[];
}

export interface ProductAudienceAssociation {
  audienceKey: string;
  reasonToUse: string;
  lang: string;
}

// FE view shape of a Product funnel objective. `id` mirrors the public HTTP
// `ID`: '' for a new objective (UCD mints the UUID on save); an existing id
// must be sent back unchanged or the update is rejected.
export interface FunnelObjective {
  id: string;
  // Browser-only identity for an objective UCD has not minted an `id` for yet,
  // so a content generation run can name it. Never sent on create/update.
  localID?: string;
  name: string;
  description: string;
  source: string;
}

export interface AudienceReference {
  key: string;
  name: string;
  country: string;
  sourceType: AudienceSourceType;
  status: ContentConfigStatus;
}

export interface ProductAudienceOption extends AudienceReference {
  eligible: boolean;
  label: string;
  unavailableReason: string;
}

// ---- Proxied entities (no Meta — read-only, sourced from UCM) ----

export interface CountryApi {
  code: string;
  name: string;
  languages?: string[];
  currencies?: string[];
  default_language?: string;
}

// FE view of a country. `langs` is the synthesised en-first order that drives
// the Product Offering `config_by_lang` editor tabs; `apiLangs` is the UCM
// whitelist exactly as UCD returned it and is the only safe source for a value
// submitted back as an association `lang`.
export interface ContentConfigCountry {
  id: string;
  name: string;
  langs: string[];
  apiLangs: string[];
  currencies: string[];
  defaultLang: string;
}

export interface VerticalApi {
  code: string;
  name: string;
  deeplink?: string;
}

// FE view of a vertical. `deeplink` is the UCM Content Portal value Content
// Injection falls back to when a Product Offering defines no deep link of its
// own; it is '' when that environment has no value configured.
export interface ContentConfigVertical {
  id: string;
  name: string;
  deeplink: string;
}
